/* Drawing frames on a canvas, in millimetres (the caller sets the scale), for the fixed-design frame pages
   (vinyl records now; CDs, coins and so on later). The moulding, mount, glass and shadows are drawn the same
   way as the made-to-measure designer (assets/mtm-builder.js); the window can be any union of rectangles and
   circles, cut through a double mount, with the record and its sleeve drawn inside.
   window.SkelfFrameDraw = { image, drawPreset, sofa } */
(function () {
  'use strict';
  var clamp = function (v, a, b) { return Math.min(b, Math.max(a, v)); };

  /* Images load once; onload asks the page to redraw */
  var images = {}, listeners = [];
  function image(url) {
    if (!url) return null;
    if (!images[url]) {
      var img = new Image();
      img.decoding = 'async';
      img.crossOrigin = 'anonymous';
      img.onload = function () { listeners.forEach(function (f) { f(); }); };
      img.src = url;
      images[url] = img;
    }
    var i = images[url];
    return i.complete && i.naturalWidth ? i : null;
  }

  function path(ctx, pts) {
    ctx.beginPath(); ctx.moveTo(pts[0][0], pts[0][1]);
    for (var i = 1; i < pts.length; i++) ctx.lineTo(pts[i][0], pts[i][1]);
    ctx.closePath();
  }

  /** One side of the moulding from its photo, repeated along the length (as the designer does). */
  function mouldingStrip(ctx, img, f, len) {
    var iw = img.naturalWidth, ih = img.naturalHeight;
    var trim = Math.round(ih * 0.03), srcH = ih - 2 * trim;
    var natural = srcH * f / iw;
    var tile = natural * clamp(160 / natural, 1, 2);
    for (var y = 0, k = 0; y < len; y += tile, k++) {
      if (k % 2) {
        ctx.save(); ctx.translate(0, y + tile); ctx.scale(1, -1);
        ctx.drawImage(img, 0, trim, iw, srcH, 0, 0, f, tile + 0.6);
        ctx.restore();
      } else ctx.drawImage(img, 0, trim, iw, srcH, 0, y, f, tile + 0.6);
    }
  }

  /** Fill the current path with a mount colour (its swatch photo tiled over its colour). */
  function fillMountPath(ctx, m, box) {
    ctx.save();
    ctx.clip();
    ctx.fillStyle = (m && m.hex) || '#f4f1ea';
    ctx.fillRect(box.x, box.y, box.w, box.h);
    var img = m && image(m.image);
    if (img) {
      ctx.globalAlpha = 0.9;
      var t = 80;
      for (var ty = box.y; ty < box.y + box.h; ty += t) for (var tx = box.x; tx < box.x + box.w; tx += t) ctx.drawImage(img, tx, ty, t + 0.3, t + 0.3);
    }
    ctx.restore();
  }

  /** The window shapes grown by d mm (rectangles keep square corners, as the cutter makes them), as one path. */
  function windowPath(ctx, windows, ox, oy, d) {
    ctx.beginPath();
    windows.forEach(function (w) {
      if (w.kind === 'circle') {
        ctx.moveTo(ox + w.x + w.r + d, oy + w.y);
        ctx.arc(ox + w.x, oy + w.y, w.r + d, 0, Math.PI * 2, false);
      } else {
        // clockwise, the same way round as the arcs, so overlapping shapes fill as one
        var x = ox + w.x - d, y = oy + w.y - d, ww = w.w + 2 * d, hh = w.h + 2 * d;
        ctx.moveTo(x, y); ctx.lineTo(x + ww, y); ctx.lineTo(x + ww, y + hh); ctx.lineTo(x, y + hh); ctx.closePath();
      }
    });
  }

  /** A record: black vinyl with grooves catching the light, a coloured label and the spindle hole. */
  function drawRecord(ctx, cx, cy, r, labelR, labelColour) {
    ctx.save();
    ctx.beginPath(); ctx.arc(cx, cy, r, 0, Math.PI * 2); ctx.fillStyle = '#111'; ctx.fill();
    // grooves: fine rings, with a wider gap between tracks
    ctx.lineWidth = 0.35;
    for (var g = labelR + 6; g < r - 2; g += 1.6) {
      var track = Math.floor((g - labelR) / ((r - labelR) / 5)) !== Math.floor((g + 1.6 - labelR) / ((r - labelR) / 5));
      ctx.strokeStyle = track ? 'rgba(255,255,255,0.10)' : 'rgba(255,255,255,0.035)';
      ctx.beginPath(); ctx.arc(cx, cy, g, 0, Math.PI * 2); ctx.stroke();
    }
    // the sheen across the vinyl
    var sheen = ctx.createLinearGradient(cx - r, cy - r, cx + r, cy + r);
    sheen.addColorStop(0.25, 'rgba(255,255,255,0)'); sheen.addColorStop(0.42, 'rgba(255,255,255,0.16)');
    sheen.addColorStop(0.5, 'rgba(255,255,255,0)'); sheen.addColorStop(0.72, 'rgba(255,255,255,0.08)'); sheen.addColorStop(0.8, 'rgba(255,255,255,0)');
    ctx.beginPath(); ctx.arc(cx, cy, r, 0, Math.PI * 2); ctx.fillStyle = sheen; ctx.fill();
    // label
    ctx.beginPath(); ctx.arc(cx, cy, labelR, 0, Math.PI * 2); ctx.fillStyle = labelColour || '#c8372d'; ctx.fill();
    ctx.lineWidth = 0.8; ctx.strokeStyle = 'rgba(255,255,255,0.55)';
    ctx.beginPath(); ctx.arc(cx, cy, labelR * 0.82, 0, Math.PI * 2); ctx.stroke();
    ctx.beginPath(); ctx.arc(cx, cy, Math.max(2.5, labelR * 0.07), 0, Math.PI * 2); ctx.fillStyle = '#efebe4'; ctx.fill();
    ctx.restore();
  }

  /** A CD: silver with a rainbow sheen, a printed face (in the label colour), the clear hub and the centre hole. */
  function drawDisc(ctx, cx, cy, r, colour) {
    ctx.save();
    ctx.beginPath(); ctx.arc(cx, cy, r, 0, Math.PI * 2);
    var silver = ctx.createRadialGradient(cx, cy, r * 0.1, cx, cy, r);
    silver.addColorStop(0, '#eef0f3'); silver.addColorStop(1, '#b8bdc5');
    ctx.fillStyle = silver; ctx.fill();
    if (ctx.createConicGradient) {
      var rb = ctx.createConicGradient(-0.6, cx, cy);
      ['rgba(255,80,80,.28)', 'rgba(255,210,80,.28)', 'rgba(90,220,120,.28)', 'rgba(80,170,255,.30)', 'rgba(190,100,255,.28)', 'rgba(255,80,80,.28)'].forEach(function (c, i, a) { rb.addColorStop(i / (a.length - 1), c); });
      ctx.beginPath(); ctx.arc(cx, cy, r, 0, Math.PI * 2); ctx.fillStyle = rb; ctx.fill();
    }
    // the printed face, leaving the shiny ring round the hub
    ctx.beginPath(); ctx.arc(cx, cy, r * 0.97, 0, Math.PI * 2); ctx.arc(cx, cy, r * 0.4, 0, Math.PI * 2, true);
    ctx.fillStyle = colour || '#2b3a55'; ctx.globalAlpha = 0.92; ctx.fill(); ctx.globalAlpha = 1;
    ctx.fillStyle = 'rgba(255,255,255,0.9)'; ctx.font = '700 ' + (r * 0.16) + 'px system-ui, sans-serif'; ctx.textAlign = 'center';
    ctx.fillText('YOUR CD', cx, cy - r * 0.55);
    // clear hub and the hole
    ctx.beginPath(); ctx.arc(cx, cy, r * 0.28, 0, Math.PI * 2); ctx.fillStyle = 'rgba(235,238,242,0.85)'; ctx.fill();
    ctx.beginPath(); ctx.arc(cx, cy, r * 0.125, 0, Math.PI * 2); ctx.fillStyle = '#ece9e2'; ctx.fill();
    ctx.lineWidth = 0.4; ctx.strokeStyle = 'rgba(0,0,0,0.25)';
    ctx.beginPath(); ctx.arc(cx, cy, r * 0.28, 0, Math.PI * 2); ctx.stroke();
    ctx.beginPath(); ctx.arc(cx, cy, r * 0.125, 0, Math.PI * 2); ctx.stroke();
    ctx.restore();
  }

  /** The sleeve: the customer's photo, or a plain designed cover until they add one. */
  function drawSleeve(ctx, x, y, s, art) {
    ctx.save();
    ctx.beginPath(); ctx.rect(x, y, s, s); ctx.clip();
    if (art) {
      var k = Math.max(s / art.naturalWidth, s / art.naturalHeight);
      var dw = art.naturalWidth * k, dh = art.naturalHeight * k;
      ctx.drawImage(art, x + (s - dw) / 2, y + (s - dh) / 2, dw, dh);
    } else {
      var g = ctx.createLinearGradient(x, y, x + s, y + s);
      g.addColorStop(0, '#2b3a55'); g.addColorStop(1, '#14192a');
      ctx.fillStyle = g; ctx.fillRect(x, y, s, s);
      // a big sun and stripes: an album cover, not anybody's in particular
      ctx.fillStyle = '#e9a23b';
      ctx.beginPath(); ctx.arc(x + s * 0.5, y + s * 0.56, s * 0.26, 0, Math.PI * 2); ctx.fill();
      ctx.fillStyle = '#14192a';
      for (var i = 0; i < 5; i++) ctx.fillRect(x, y + s * (0.6 + i * 0.045), s, s * (0.012 + i * 0.004));
      ctx.fillStyle = 'rgba(255,255,255,0.9)';
      ctx.font = '700 ' + (s * 0.075) + 'px system-ui, sans-serif';
      ctx.textAlign = 'center';
      ctx.fillText('YOUR ALBUM', x + s / 2, y + s * 0.17);
    }
    ctx.restore();
  }

  /** A shadow falling just inside the cut-out: the mount's edge, drawn off-canvas, casts it into the window. */
  var shadowCanvas = null;
  function innerShadow(ctx, windows, ox, oy, px) {
    var cv = ctx.canvas;
    if (!cv || !ctx.getTransform) return;
    shadowCanvas = shadowCanvas || document.createElement('canvas');
    if (shadowCanvas.width !== cv.width || shadowCanvas.height !== cv.height) { shadowCanvas.width = cv.width; shadowCanvas.height = cv.height; }
    var o = shadowCanvas.getContext('2d');
    o.setTransform(1, 0, 0, 1, 0, 0); o.clearRect(0, 0, cv.width, cv.height);
    o.setTransform(ctx.getTransform());
    o.globalCompositeOperation = 'source-over';
    o.fillStyle = '#000'; o.fillRect(-10000, -10000, 20000, 20000);
    o.globalCompositeOperation = 'destination-out';
    windowPath(o, windows, ox, oy, 0); o.fill();
    o.globalCompositeOperation = 'source-over';
    var k = px.scale * px.dpr;
    ctx.save();
    windowPath(ctx, windows, ox, oy, 0); ctx.clip();
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.shadowColor = 'rgba(0,0,0,0.5)';
    ctx.shadowBlur = Math.max(2, 2.5 * k);
    ctx.shadowOffsetX = 0.6 * k; ctx.shadowOffsetY = 1 * k;
    ctx.drawImage(shadowCanvas, 0, 0);
    ctx.restore();
  }

  /** The moulding round the outside: four mitred sides from the photo, with light and shade. */
  function drawMoulding(ctx, W, H, f, img, px) {
    var sides = [
      { poly: [[0, 0], [f, f], [f, H - f], [0, H]], tx: 0, ty: 0, rot: 0, len: H, light: 'rgba(255,255,255,0.05)' },
      { poly: [[0, 0], [W, 0], [W - f, f], [f, f]], tx: W, ty: 0, rot: Math.PI / 2, len: W, light: 'rgba(255,255,255,0.12)' },
      { poly: [[W, 0], [W, H], [W - f, H - f], [W - f, f]], tx: W, ty: H, rot: Math.PI, len: H, light: 'rgba(0,0,0,0.10)' },
      { poly: [[0, H], [f, H - f], [W - f, H - f], [W, H]], tx: 0, ty: H, rot: -Math.PI / 2, len: W, light: 'rgba(0,0,0,0.20)' }
    ];
    sides.forEach(function (s) {
      ctx.save();
      path(ctx, s.poly); ctx.clip();
      ctx.translate(s.tx, s.ty); ctx.rotate(s.rot);
      if (img) mouldingStrip(ctx, img, f, s.len);
      else {
        var g = ctx.createLinearGradient(0, 0, f, 0); g.addColorStop(0, '#4a3b2f'); g.addColorStop(1, '#2c221b');
        ctx.fillStyle = g; ctx.fillRect(0, 0, f, s.len);
      }
      ctx.restore();
      ctx.save(); path(ctx, s.poly); ctx.fillStyle = s.light; ctx.fill(); ctx.restore();
    });
    ctx.save();
    ctx.lineWidth = Math.max(0.25, 0.7 / px.scale);
    ctx.strokeStyle = 'rgba(0,0,0,0.28)';
    ctx.beginPath();
    ctx.moveTo(0, 0); ctx.lineTo(f, f); ctx.moveTo(W, 0); ctx.lineTo(W - f, f);
    ctx.moveTo(W, H); ctx.lineTo(W - f, H - f); ctx.moveTo(0, H); ctx.lineTo(f, H - f);
    ctx.stroke();
    ctx.strokeStyle = 'rgba(0,0,0,0.35)';
    ctx.strokeRect(0, 0, W, H);
    ctx.strokeRect(f, f, W - 2 * f, H - 2 * f);
    ctx.restore();
  }

  /**
   * A fixed-design frame. sp: preset (glassW/H, windows, revealMm, sample sizes), fw (moulding width),
   * moulding (Image), outerMount / innerMount ({hex, image}), glass ('clear' | 'styrene' | 'nr' | 'art' | 'none'),
   * art (the customer's sleeve photo, an Image) and labelColour. Size of the drawing: outside of the frame.
   */
  function drawPreset(ctx, sp, px) {
    var p = sp.preset, f = sp.fw;
    var REBATE = 5;
    var lip = Math.max(0, f - REBATE);               // moulding seen beyond the glass on each side
    var W = p.glassW + 2 * lip, H = p.glassH + 2 * lip;
    var ox = lip, oy = lip;                           // the mount board's top-left inside the drawing
    var ix = f, iy = f, iw = W - 2 * f, ih = H - 2 * f; // what's visible inside the moulding

    // Shadow on the wall
    ctx.save();
    ctx.shadowColor = 'rgba(30, 25, 20, 0.32)';
    ctx.shadowBlur = 28 * px.dpr; ctx.shadowOffsetY = 12 * px.dpr;
    ctx.fillStyle = '#222'; ctx.fillRect(0, 0, W, H);
    ctx.restore();

    ctx.save();
    ctx.beginPath(); ctx.rect(ix, iy, iw, ih); ctx.clip();
    var box = { x: ix, y: iy, w: iw, h: ih };
    // Outer mount over everything
    ctx.beginPath(); ctx.rect(ix, iy, iw, ih); fillMountPath(ctx, sp.outerMount, box);
    var reveal = p.revealMm || 4;
    // The outer mount's bevel: its white core shows as a thin line round the cut
    windowPath(ctx, p.windows, ox, oy, reveal + 1.4); ctx.fillStyle = '#fbfaf6'; ctx.fill();
    // The inner mount, showing round the window
    windowPath(ctx, p.windows, ox, oy, reveal); fillMountPath(ctx, sp.innerMount, box);
    // ...and its own bevel
    windowPath(ctx, p.windows, ox, oy, 1.1); ctx.fillStyle = '#f4f2ec'; ctx.fill();

    // Inside the window: the record behind, the sleeve in front
    ctx.save();
    windowPath(ctx, p.windows, ox, oy, 0); ctx.clip();
    ctx.fillStyle = '#1a1a1a'; ctx.fillRect(ix, iy, iw, ih);
    var rect = p.windows.filter(function (w) { return w.kind !== 'circle'; })[0];
    var circ = p.windows.filter(function (w) { return w.kind === 'circle'; })[0];
    var s = p.sample || { sleeveMm: rect ? rect.w + 9 : 300, recordMm: circ ? circ.r * 2 + 4 : 300, labelMm: 100 };
    if (circ && p.kind === 'cd') drawDisc(ctx, ox + circ.x, oy + circ.y, s.recordMm / 2, sp.labelColour);
    else if (circ) drawRecord(ctx, ox + circ.x, oy + circ.y, s.recordMm / 2, s.labelMm / 2, sp.labelColour);
    if (rect) {
      var vivid = sp.glass === 'art' && typeof ctx.filter === 'string';
      if (vivid) ctx.filter = 'saturate(1.2) contrast(1.07) brightness(1.05)';
      drawSleeve(ctx, ox + rect.x + rect.w / 2 - s.sleeveMm / 2, oy + rect.y + rect.h / 2 - s.sleeveMm / 2, s.sleeveMm, sp.art);
      if (vivid) ctx.filter = 'none';
    }
    ctx.restore();
    // A soft shadow just inside the cut (light from the top left)
    innerShadow(ctx, p.windows, ox, oy, px);

    // The frame's own shadow inside the moulding
    var sh = Math.min(10, f * 0.5);
    var gT = ctx.createLinearGradient(0, iy, 0, iy + sh); gT.addColorStop(0, 'rgba(0,0,0,0.28)'); gT.addColorStop(1, 'rgba(0,0,0,0)');
    ctx.fillStyle = gT; ctx.fillRect(ix, iy, iw, sh);
    var gL = ctx.createLinearGradient(ix, 0, ix + sh, 0); gL.addColorStop(0, 'rgba(0,0,0,0.2)'); gL.addColorStop(1, 'rgba(0,0,0,0)');
    ctx.fillStyle = gL; ctx.fillRect(ix, iy, sh, ih);
    // Glass: a reflection on ordinary glass and styrene; none on non-reflective or Art Glass
    var look = sp.glass || 'clear';
    if (look === 'clear' || look === 'styrene') {
      var soft = look === 'styrene';
      ctx.fillStyle = soft ? 'rgba(255,255,255,0.05)' : 'rgba(255,255,255,0.035)';
      ctx.fillRect(ix, iy, iw, ih);
      var gs = ctx.createLinearGradient(ix, iy, ix + iw, iy + ih);
      gs.addColorStop(0, 'rgba(255,255,255,0)'); gs.addColorStop(0.36, 'rgba(255,255,255,0)');
      gs.addColorStop(0.45, soft ? 'rgba(255,255,255,0.08)' : 'rgba(255,255,255,0.13)'); gs.addColorStop(0.54, 'rgba(255,255,255,0)');
      gs.addColorStop(0.6, 'rgba(255,255,255,0)'); gs.addColorStop(0.64, soft ? 'rgba(255,255,255,0.03)' : 'rgba(255,255,255,0.06)'); gs.addColorStop(0.68, 'rgba(255,255,255,0)');
      ctx.fillStyle = gs; ctx.fillRect(ix, iy, iw, ih);
    }
    ctx.restore();

    if (f > 0) drawMoulding(ctx, W, H, f, sp.moulding, px);
    return { w: W, h: H };
  }

  /** The outside size of a preset frame with a moulding f mm wide. */
  function outerSize(p, f) {
    var lip = Math.max(0, f - 5);
    return { w: p.glassW + 2 * lip, h: p.glassH + 2 * lip };
  }

  function roundRect(ctx, x, y, w, h, r) {
    ctx.beginPath();
    ctx.moveTo(x + r, y); ctx.arcTo(x + w, y, x + w, y + h, r); ctx.arcTo(x + w, y + h, x, y + h, r);
    ctx.arcTo(x, y + h, x, y, r); ctx.arcTo(x, y, x + w, y, r); ctx.closePath();
  }
  /** A 2m sofa at the same scale, for the "on a wall" view (as the designer draws it). */
  function sofa(ctx, x, floor, s) {
    ctx.save();
    var r = 60 * s;
    ctx.fillStyle = '#7d8a83'; roundRect(ctx, x, floor - 820 * s, 2000 * s, 420 * s, r); ctx.fill();
    ctx.fillStyle = '#86948c';
    roundRect(ctx, x - 40 * s, floor - 560 * s, 260 * s, 420 * s, r); ctx.fill();
    roundRect(ctx, x + 1780 * s, floor - 560 * s, 260 * s, 420 * s, r); ctx.fill();
    ctx.fillStyle = '#909e96'; roundRect(ctx, x + 180 * s, floor - 480 * s, 1640 * s, 230 * s, r); ctx.fill();
    ctx.fillStyle = '#5d675f';
    ctx.fillRect(x + 80 * s, floor - 140 * s, 30 * s, 140 * s);
    ctx.fillRect(x + 1890 * s, floor - 140 * s, 30 * s, 140 * s);
    ctx.restore();
  }

  window.SkelfFrameDraw = {
    image: image,
    onImage: function (f) { listeners.push(f); },
    drawPreset: drawPreset,
    outerSize: outerSize,
    sofa: sofa
  };
})();
